/* Lightweight attribution + analytics helpers.
 *
 * As of Feb 2026 this file is GA4-only. Meta Pixel and Pinterest Tag were
 * removed site-wide because no paid campaigns are running against them.
 * The SHA-256 hashed-email code was ALSO removed — it was only used to
 * improve Meta/Pinterest match quality, was never disclosed to submitters,
 * and no longer has any receiver.
 *
 * We now push events only into window.gtag / window.dataLayer (first-party
 * GA4 + GTM). No third-party ad-network beacons.
 */

const STORAGE_KEY = "shescan.attribution";
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

function safeStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function captureAttribution() {
  if (typeof window === "undefined") return {};
  const storage = safeStorage();
  // If we already captured this session, keep the original (first-touch wins).
  if (storage) {
    const existing = storage.getItem(STORAGE_KEY);
    if (existing) {
      try {
        return JSON.parse(existing);
      } catch {
        /* fall through and re-capture */
      }
    }
  }

  const params = new URLSearchParams(window.location.search);
  const data = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) data[k] = v.slice(0, 200);
  });
  data.referrer = (document.referrer || "").slice(0, 500);
  data.landing_path = window.location.pathname;
  data.captured_at = new Date().toISOString();

  if (storage) {
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      /* ignore */
    }
  }
  return data;
}

export function getAttribution() {
  if (typeof window === "undefined") return {};
  const storage = safeStorage();
  if (!storage) return {};
  try {
    return JSON.parse(storage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

/**
 * Fire a Lead conversion event to GA4 (`generate_lead`).
 *
 * No email is transmitted (hashed or otherwise). We pass the lead_id (Mongo
 * uuid), the sourcing category, and UTM attribution — nothing that identifies
 * the submitter to third parties.
 */
export function fireLead({ id, shop, value = 0, currency = "USD" } = {}) {
  const attr = getAttribution();
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "generate_lead", {
        currency,
        value,
        method: "beta_signup",
        lead_id: id,
        shop,
        utm_source: attr.utm_source || "",
        utm_medium: attr.utm_medium || "",
        utm_campaign: attr.utm_campaign || "",
        utm_content: attr.utm_content || "",
        utm_term: attr.utm_term || "",
      });
      window.gtag("event", "form_submit", {
        form_name: "beta_signup",
        utm_content: attr.utm_content || "",
      });
    }
  } catch {
    /* swallow — analytics failures must never break UX */
  }
}
